import { ApiService } from '../../services/ApiService';
import {
	containsDynamicDate,
	getBrowserTimezone,
	processDynamicDatesInObject,
	resolveDynamicDate,
	resolveDynamicDateTimeEnd,
	resolveDynamicDateTimeStart,
} from '../../utils/dynamic-date';
import { FilterRegistry } from '../registry';
import type { FilterConfig, FilterOption, FilterState } from '../types';

export interface TransformFiltersOptions {
	/**
	 * List key for filter validation and listKey-aware filtering
	 */
	listKey?: string;
	/**
	 * Whether to resolve dynamic dates like {{TODAY}}, {{DATE:-5d}}. Default: true
	 */
	resolveDynamicDates?: boolean;
}

export interface QueryResult {
	data: any[];
	error?: string;
	success: boolean;
}

/**
 * FilterService - Unified backend abstraction for filters
 *
 * Provides a single interface for all filter-related API calls
 * and query parameter construction.
 */
export class FilterService {
	/**
	 * Generic API call helper using shared ApiService
	 */
	private static async apiCall(
		url: string,
		params: Record<string, string> = {},
	): Promise<QueryResult> {
		try {
			// apiGet defaults to unknown — explicitly bind the response shape so
			// downstream `.success / .data / .error` access compiles cleanly.
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<unknown>;
				error?: string;
			}>(url, params);

			return {
				data: response.success && response.data ? response.data : [],
				...(response.error && { error: response.error }),
				success: response.success,
			};
		} catch (error) {
			return {
				data: [],
				error:
					error instanceof Error ? error.message : 'Unknown error occurred',
				success: false,
			};
		}
	}

	/**
	 * Get filter options for a specific filter type
	 */
	static async getFilterOptions(
		filterType: string,
		searchTerm?: string,
		isConfigMode: boolean = false,
		filterContext?: Record<string, any>,
	): Promise<FilterOption[]> {
		const config = FilterRegistry.getConfig(filterType);

		if (!config) {
			// // Error: Filter configuration not found for type
			return [];
		}

		// Return static options for non-searchable filters
		if (!config.searchable && config.options) {
			return config.options;
		}

		// Make API call for searchable filters
		if (config.apiEndpoint) {
			const params: Record<string, string> = {};
			// Send search term - either via filters JSON or as query param
			if (config.searchFilterName != null && searchTerm) {
				// Send search via filters JSON (e.g., for clients endpoint that expects clientSearch filter)
				params.filters = JSON.stringify({
					[config.searchFilterName]: searchTerm,
				});
			} else if (filterType === 'geography') {
				// Special handling for geography filter - always send 'q' parameter
				params.q = searchTerm || '';
			} else {
				params.searchTerm = searchTerm || '';
			}

			// For salespeople filter in config mode, include all users
			if (filterType === 'salespeople' && isConfigMode) {
				params.includeAll = 'true';
			}

			// PD-14109: view-scoped (faceted) counts. When the host list passes its
			// other active filters (serialized by BaseMultiSelect via
			// transformFiltersForBackend) plus its list key, forward them so the
			// options endpoint can scope each option's count to the current view.
			// Generic — driven by the filterContext payload, not the filter name; an
			// endpoint that ignores these params simply returns its global count.
			if (filterContext?.crossFilters != null) {
				params.crossFilters = String(filterContext.crossFilters);
			}
			if (filterContext?.scopeListKey != null) {
				params.scopeListKey = String(filterContext.scopeListKey);
			}

			// For subcategory filter, include selected categories and manufacturers as context
			if (filterType === 'subcategory' && filterContext?.category) {
				const categories = Array.isArray(filterContext.category)
					? filterContext.category
					: [filterContext.category];
				params.category = categories.join(',');
			}
			if (filterType === 'subcategory' && filterContext?.manufacturer) {
				const manufacturers = Array.isArray(filterContext.manufacturer)
					? filterContext.manufacturer
					: [filterContext.manufacturer];
				params.manufacturer = manufacturers.join(',');
			}

			// For manufacturer filter, include selected categories and subcategories as context
			if (filterType === 'manufacturer' && filterContext?.category) {
				const categories = Array.isArray(filterContext.category)
					? filterContext.category
					: [filterContext.category];
				params.category = categories.join(',');
			}
			if (filterType === 'manufacturer' && filterContext?.subcategory) {
				const subcategories = Array.isArray(filterContext.subcategory)
					? filterContext.subcategory
					: [filterContext.subcategory];
				params.subcategory = subcategories.join(',');
			}

			// For category filter, include selected manufacturers as context
			if (filterType === 'category' && filterContext?.manufacturer) {
				const manufacturers = Array.isArray(filterContext.manufacturer)
					? filterContext.manufacturer
					: [filterContext.manufacturer];
				params.manufacturer = manufacturers.join(',');
			}

			// Include any additional API params from config. Values may contain
			// `{key}` placeholders resolved from the hosting list's context
			// (PD-14078 — e.g. `{clientId}` scopes the Child Accounts filter's
			// collection to the viewed client). An unresolvable placeholder
			// aborts the load: an UNscoped collection would be worse than none.
			if (config.apiParams != null) {
				for (const [paramKey, paramValue] of Object.entries(config.apiParams)) {
					let resolved = paramValue;
					const placeholders = paramValue.match(/\{(\w+)\}/g) ?? [];
					for (const placeholder of placeholders) {
						const contextKey = placeholder.slice(1, -1);
						const contextValue: unknown = filterContext?.[contextKey];
						if (contextValue == null) {
							return [];
						}
						resolved = resolved.replace(
							placeholder,
							String(contextValue as string | number),
						);
					}
					// `filters` may already hold the search term (searchFilterName
					// path above) — merge the two JSON objects instead of clobbering
					// search with the config's scoping filters (or vice versa).
					if (paramKey === 'filters' && params.filters != null) {
						try {
							params.filters = JSON.stringify({
								...JSON.parse(params.filters),
								...JSON.parse(resolved),
							});
						} catch {
							params[paramKey] = resolved;
						}
					} else {
						params[paramKey] = resolved;
					}
				}
			}

			const result = await this.apiCall(config.apiEndpoint, params);

			if (result.success) {
				const transformedOptions = FilterRegistry.transformToFilterOptions(
					result.data,
					config,
				);

				// For salespeople filter, return all users including the current user
				// No special "ME" handling needed

				return transformedOptions;
			} else {
				// // Error: Failed to fetch filter options
				return [];
			}
		}

		return [];
	}

	/**
	 * Build query parameters from filter state
	 */
	static buildQueryParams(filters: FilterState): Record<string, string> {
		const params: Record<string, string> = {};

		Object.entries(filters).forEach(([filterType, values]) => {
			if (!values || !Array.isArray(values) || values.length === 0) {
				return;
			}

			const config = FilterRegistry.getConfig(filterType);
			if (!config) {
				return;
			}

			const paramName = filterType;

			if (config.type === 'radio' || !config.multiple) {
				// Single value filters
				const idValue = String(values[0]?.id || '');
				params[paramName] = idValue;
			} else {
				// Multi-value filters - join with commas
				const ids = values.map((v) => String(v.id)).filter(Boolean);

				if (ids.length > 0) {
					params[paramName] = ids.join(',');
				}
			}
		});

		return params;
	}

	/**
	 * Get filtered report data
	 */
	static async getFilteredData(
		endpoint: string,
		filters: FilterState,
		additionalParams: Record<string, string> = {},
	): Promise<QueryResult> {
		const filterParams = this.buildQueryParams(filters);
		const allParams = { ...additionalParams, ...filterParams };

		return this.apiCall(endpoint, allParams);
	}

	/**
	 * Resolve dynamic values like {{TODAY}}, {{DATE:-5d}}, ME, etc.
	 * For date filters, uses timezone-aware datetime boundaries.
	 */
	private static resolveDynamicValue(
		value: unknown,
		config?: FilterConfig,
		resolveDates: boolean = true,
	): unknown {
		if (typeof value === 'string') {
			// User context tokens are passed through - resolved by TokenResolverService on backend
			// PD-13742: MY_WAREHOUSE resolves to the caller's active warehouse
			// server-side (same pass-through pattern as ME).
			if (
				value === 'ME' ||
				value === 'CURRENT_CUSTOMER' ||
				value === 'MY_WAREHOUSE'
			) {
				return value;
			}

			// For date filters with dynamic date expressions, use timezone-aware datetime boundaries
			if (
				resolveDates &&
				config?.type === 'date' &&
				containsDynamicDate(value)
			) {
				const browserTimezone = getBrowserTimezone();
				const paramName = config.queryParamName ?? '';

				// "From" filters use start of day, "To" filters use end of day
				if (
					paramName.endsWith('From') ||
					paramName.toLowerCase().includes('from')
				) {
					return resolveDynamicDateTimeStart(value, browserTimezone);
				}

				if (
					paramName.endsWith('To') ||
					paramName.toLowerCase().includes('to')
				) {
					return resolveDynamicDateTimeEnd(value, browserTimezone);
				}

				// Default to start of day for other date filters
				return resolveDynamicDateTimeStart(value, browserTimezone);
			}

			// Use the centralized dynamic date resolver for non-date filters
			if (resolveDates && containsDynamicDate(value)) {
				return resolveDynamicDate(value);
			}
		}

		// Handle objects by recursively processing them (for date-range filters with nested startDate/endDate)
		if (resolveDates && typeof value === 'object' && value !== null) {
			return processDynamicDatesInObject(value);
		}

		return value;
	}

	/**
	 * Transform filter state to backend query format.
	 * Uses filter config as source of truth for how to format each filter.
	 *
	 * @param filters - The filter state to transform
	 * @param options - Optional configuration for transformation
	 */
	static transformFiltersForBackend(
		filters: FilterState,
		options?: TransformFiltersOptions,
	): Record<string, unknown> {
		const result: Record<string, unknown> = {};
		const listKey = options?.listKey;
		const resolveDates = options?.resolveDynamicDates !== false; // Default to true

		/**
		 * Extract value from a filter option using config settings.
		 * Handles both object-style options ({id, label}) and raw values (backward compat).
		 */
		const extractValue = (item: unknown, config: FilterConfig): unknown => {
			if (item == null) {
				return null;
			}

			let rawValue: unknown;

			// Support for valueExtractor.extract function (priority over property)
			if (config.valueExtractor?.extract != null) {
				rawValue = config.valueExtractor.extract(item);
			} else {
				// Use property path if provided
				const property = config.valueExtractor?.property ?? 'id';

				if (typeof item === 'object' && item !== null) {
					if (property in item) {
						rawValue = (item as Record<string, unknown>)[property];
					} else if (property !== 'id' && 'id' in item) {
						// FilterOption objects store the mapped value in 'id' via displayFields.
						// Fall back to 'id' when the configured property (e.g., '_id') is missing,
						// which happens when filters are loaded from saved searches.
						rawValue = (item as Record<string, unknown>).id;
					} else {
						rawValue = item;
					}
				} else {
					rawValue = item; // Fallback for raw values (backward compat)
				}
			}

			if (rawValue == null) {
				return null;
			}

			// Resolve dynamic values (dates, user tokens)
			rawValue = this.resolveDynamicValue(rawValue, config, resolveDates);

			// Apply type transformation if specified
			if (config.valueExtractor?.transform === 'number') {
				const num = Number(rawValue);

				return isNaN(num) ? null : num;
			}

			// For string transform or default, convert to string unless it's already a resolved object
			if (typeof rawValue === 'object') {
				return rawValue; // Keep objects as-is (e.g., resolved date ranges)
			}

			return String(rawValue);
		};

		for (const [filterType, rawValues] of Object.entries(filters)) {
			if (rawValues == null) {
				continue;
			}

			// Normalize to array format - handle both FilterState (arrays) and raw values
			// This allows callers to pass either [{id, label, data}] or just {startDate, endDate}
			const values = Array.isArray(rawValues) ? rawValues : [rawValues];

			if (values.length === 0) {
				continue;
			}

			const config = FilterRegistry.getConfig(filterType);

			if (config == null) {
				continue;
			}

			// Routing params bypass listKey check - they determine which endpoint/view to use
			const routingParams = ['listType', 'viewType'];
			if (routingParams.includes(filterType)) {
				const firstValue = values[0];

				if (
					typeof firstValue === 'object' &&
					firstValue !== null &&
					'id' in firstValue
				) {
					result[filterType] = (firstValue as { id: unknown }).id;
				} else {
					result[filterType] = firstValue;
				}

				continue;
			}

			// If listKey is specified, check if this filter applies to the list key
			if (
				listKey != null &&
				config.listKeys != null &&
				config.listKeys.length > 0 &&
				!config.listKeys.includes(listKey)
			) {
				continue;
			}

			const paramName = filterType;
			const isSingleValue =
				config.format === 'single' ||
				config.type === 'radio' ||
				config.type === 'single-select';

			// Extract values from all items
			const extractedValues = values
				.map((v) => extractValue(v, config))
				.filter((v): v is string | number | object => v != null);

			// Apply custom transformation if defined
			if (config.customTransform != null && extractedValues.length > 0) {
				const transformed = config.customTransform(
					extractedValues,
					listKey ?? '',
				);

				if (transformed != null) {
					result[paramName] = transformed;
				}

				continue;
			}

			if (isSingleValue) {
				// Single value filters (radio, single-select, text)
				const firstValue = extractedValues[0];

				// Skip 'All' values for radio/single-select (case-insensitive)
				if (
					(typeof firstValue === 'string' &&
						firstValue.toLowerCase() === 'all') ||
					firstValue == null
				) {
					continue;
				}

				result[paramName] = firstValue;
			} else {
				// Multi-value filters (multi-select)
				if (extractedValues.length > 0) {
					result[paramName] = extractedValues;
				}
			}

			// KEEP: productCategories → categories backward compat (backend not standardized)
			if (filterType === 'productCategories' && result[paramName] != null) {
				result['categories'] = result[paramName];
			}
		}

		return result;
	}

	/**
	 * Helper method to check if any filters are active
	 */
	static hasActiveFilters(filters: FilterState): boolean {
		return Object.values(filters).some(
			(filterValues) => Array.isArray(filterValues) && filterValues.length > 0,
		);
	}

	/**
	 * Get summary of active filters for display
	 */
	static getFilterSummary(filters: FilterState): string[] {
		const summary: string[] = [];

		Object.entries(filters).forEach(([filterType, values]) => {
			if (!values || !Array.isArray(values) || values.length === 0) {
				return;
			}

			const config = FilterRegistry.getConfig(filterType);
			if (!config) {
				return;
			}

			if (values.length === 1 && values[0]) {
				summary.push(`${config.label}: ${values[0].label}`);
			} else if (values.length > 0) {
				summary.push(`${config.label}: ${values.length} selected`);
			}
		});

		return summary;
	}

	/**
	 * Clear all filters
	 */
	static getEmptyFilterState(): FilterState {
		return {};
	}

	/**
	 * Validate filter state against configurations
	 */
	static validateFilterState(filters: FilterState): {
		valid: boolean;
		errors: string[];
	} {
		const errors: string[] = [];

		Object.entries(filters).forEach(([filterType, values]) => {
			const config = FilterRegistry.getConfig(filterType);

			if (!config) {
				errors.push(`Unknown filter type: ${filterType}`);
				return;
			}

			const isArray = Array.isArray(values);

			if (config.required && (!values || !isArray || values.length === 0)) {
				errors.push(`${config.label} is required`);
			}

			if (!config.multiple && isArray && values.length > 1) {
				errors.push(`${config.label} allows only one selection`);
			}
		});

		return {
			valid: errors.length === 0,
			errors,
		};
	}
}
